import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import VideoCard from "@/components/feature/VideoCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useSEO } from "@/hooks/useSEO";
import { categoryList, regionList, videoList, yearList } from "@/mocks/videos";

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";

  useSEO({
    title: query ? `“${query}”的搜索结果 - 桔子视频` : "搜索影视内容 - 桔子视频",
    description: query
      ? `在桔子视频搜索“${query}”相关的电影、电视剧、综艺等影视内容，支持多条件筛选与在线观看。`
      : "在桔子视频搜索电影、电视剧、综艺等多类别影视内容，支持多条件筛选与在线观看。",
    keywords: query
      ? `${query},搜索,桔子视频,在线观看`
      : "桔子视频,影视搜索,在线观看",
  });

  const [input, setInput] = useState(query);
  const [typeFilter, setTypeFilter] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState<number | "全部">("全部");

  useEffect(() => {
    setInput(query);
  }, [query]);

  useEffect(() => {
    setTypeFilter("全部");
    setRegion("全部");
    setYear("全部");
  }, [query]);

  const results = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    if (!keyword) return [];
    let list = videoList.filter((item) => {
      const haystack = [
        item.title,
        item.typeName,
        item.region,
        item.director,
        ...item.genres,
        ...item.actors,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(keyword);
    });
    if (typeFilter !== "全部") list = list.filter((item) => item.typeKey === typeFilter);
    if (region !== "全部") list = list.filter((item) => item.region === region);
    if (year !== "全部") list = list.filter((item) => item.year === year);
    return list;
  }, [query, typeFilter, region, year]);

  const hotRecommend = useMemo(
    () => [...videoList].sort((a, b) => b.rating - a.rating).slice(0, 5),
    [],
  );

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const value = input.trim();
    setSearchParams(value ? { q: value } : {});
  };

  const resetFilters = () => {
    setTypeFilter("全部");
    setRegion("全部");
    setYear("全部");
  };

  const ChipGroup = ({
    label,
    options,
    value,
    onPick,
  }: {
    label: string;
    options: { value: string | number; label: string }[];
    value: string | number;
    onPick: (v: string | number) => void;
  }) => (
    <div className="flex flex-col gap-2 py-3 md:flex-row md:items-center md:gap-4">
      <span className="w-14 shrink-0 text-sm text-foreground-500">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={String(option.value)}
            type="button"
            onClick={() => onPick(option.value)}
            className={`cursor-pointer rounded-md px-3 py-1.5 text-sm transition-colors ${
              value === option.value
                ? "bg-primary-500 text-background-50"
                : "text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="mx-auto max-w-[1280px] px-4 pt-6 md:px-6 md:pt-8">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索结果" }]} />

        <form
          onSubmit={handleSubmit}
          className="mt-5 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-4 sm:flex-row sm:items-center md:p-5"
        >
          <div className="flex flex-1 items-center rounded-full border border-background-200 bg-background-100 px-3 py-2">
            <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-base" />
            </span>
            <input
              value={input}
              onChange={(event) => setInput(event.target.value)}
              type="text"
              placeholder="输入影片、演员或类型关键词"
              aria-label="搜索关键词"
              className="w-full bg-transparent px-2 text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
            />
          </div>
          <button
            type="submit"
            className="flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-search-line" />
            搜索
          </button>
        </form>

        <p className="mt-4 text-sm text-foreground-600">
          {query ? (
            <>
              关键词 <span className="font-semibold text-foreground-950">“{query}”</span>{" "}
              共找到 <span className="font-semibold text-primary-600">{results.length}</span> 个相关视频
            </>
          ) : (
            "请输入关键词开始搜索影视内容"
          )}
        </p>
      </div>

      <div className="mx-auto mt-6 max-w-[1280px] px-4 md:px-6">
        <div className="rounded-lg border border-background-200 bg-background-50 px-4 py-2 md:px-5">
          <ChipGroup
            label="类型"
            options={[
              { value: "全部", label: "全部" },
              ...categoryList.map((c) => ({ value: c.key, label: c.name })),
            ]}
            value={typeFilter}
            onPick={(v) => setTypeFilter(String(v))}
          />
          <div className="border-t border-background-200" />
          <ChipGroup
            label="地区"
            options={regionList.map((r) => ({ value: r, label: r }))}
            value={region}
            onPick={(v) => setRegion(String(v))}
          />
          <div className="border-t border-background-200" />
          <ChipGroup
            label="年份"
            options={["全部", ...yearList].map((y) => ({
              value: y,
              label: y === "全部" ? "全部" : `${y}`,
            }))}
            value={year}
            onPick={(v) => setYear(v === "全部" ? "全部" : Number(v))}
          />
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
        {results.length > 0 ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {results.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
        ) : (
          <div>
            <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 py-14 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
                <i className="ri-emotion-unhappy-line" />
              </span>
              <h2 className="mt-4 text-lg font-semibold text-foreground-900">未找到相关内容</h2>
              <p className="mt-2 text-sm text-foreground-500">
                {query ? "换个关键词试试，或调整下方筛选条件。" : "输入关键词即可发现更多精彩内容。"}
              </p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-6 cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                重置筛选
              </button>
            </div>

            <div className="mt-10">
              <h2 className="text-lg font-bold text-foreground-950 font-heading">热门推荐</h2>
              <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                {hotRecommend.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mx-auto max-w-[1280px] px-4 pb-10 md:px-6">
        <div className="flex flex-wrap items-center gap-3 rounded-lg bg-background-100 px-5 py-4 text-sm text-foreground-600">
          <span className="flex items-center gap-1.5 font-medium text-foreground-900">
            <i className="ri-fire-line text-primary-600" />
            大家都在搜
          </span>
          {["星际远征", "长安夜行", "科幻", "综艺", "纪录片 ocean"].map((item) => (
            <Link
              key={item}
              to={`/search?q=${encodeURIComponent(item)}`}
              className="whitespace-nowrap rounded-full bg-background-50 px-3 py-1 text-xs text-foreground-700 transition-colors hover:text-primary-600"
            >
              {item}
            </Link>
          ))}
        </div>
      </div>
    </Layout>
  );
}